import { CobaltAvatar } from '@cae/cobalt-react/components/avatar';
import { CobaltBreadcrumb } from '@cae/cobalt-react/components/breadcrumb';
import { CobaltPageHeader } from '@cae/cobalt-react/components/page-header';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useVisibleProfileNavigationItems } from '@features/profile/hooks/use-visible-profile-navigation-items/useVisibleProfileNavigationItems';
import { APP_ROUTES } from '@routes/routes.constants';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useProfile } from '../../hooks/use-profile/useProfile';
import styles from './ProfileHeader.module.css';
import { getProfileSubPageTitle } from './ProfileHeader.utils';

const ProfileHeader = React.memo((): React.ReactElement => {
  const { profile, isProfileError, isProfileLoading } = useProfile();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const location = useLocation();
  const navigate = useNavigate();
  const visibleNavigationItems = useVisibleProfileNavigationItems();
  const shouldShowProfileIdentity =
    !isProfileLoading && !isProfileError && !!profile;

  const isMenuPage = location.pathname === APP_ROUTES.PROFILE;
  const isMobileSubPage = isMobile && !isMenuPage;
  const breadcrumbLabel = isMobileSubPage ? 'Back' : 'Back to app';

  const handleBackClick = React.useCallback((): void => {
    navigate(isMobileSubPage ? APP_ROUTES.PROFILE : APP_ROUTES.APP_INITIALIZER);
  }, [navigate, isMobileSubPage]);

  const breadcrumbs = (
    <CobaltBreadcrumb
      aria-label={breadcrumbLabel}
      variant="back-navigation"
      onBackClick={handleBackClick}
    >
      {breadcrumbLabel}
    </CobaltBreadcrumb>
  );

  if (isMobileSubPage) {
    return (
      <CobaltPageHeader
        data-heap-id="profile-header"
        title={getProfileSubPageTitle(
          location.pathname,
          visibleNavigationItems
        )}
        breadcrumbs={breadcrumbs}
        className={styles.mobileProfileHeader}
      />
    );
  }

  return (
    <CobaltPageHeader
      data-heap-id="profile-header"
      title="Profile settings"
      subtitle="Here, you can set all of your profile settings."
      breadcrumbs={breadcrumbs}
      className={styles.profileHeader}
      content={
        isProfileLoading ? (
          <div className={styles.profileStatusMessage}>
            Loading profile details...
          </div>
        ) : isProfileError ? (
          <div className={styles.profileStatusMessageError} role="alert">
            Unable to load profile details.
          </div>
        ) : shouldShowProfileIdentity ? (
          <div className={styles.profileHeaderAvatar}>
            <CobaltAvatar
              firstName={profile.givenName}
              lastName={profile.lastName}
              size="large"
              color="neutral"
              className={styles.avatarInitials}
            />
            <div className={styles.profileInformation}>
              <div className="heading-02">
                {profile.givenName} {profile.lastName}
              </div>
              <div className={`body-02 ${styles.profileEmail}`}>
                {profile.email}
              </div>
            </div>
          </div>
        ) : null
      }
    />
  );
});

ProfileHeader.displayName = 'ProfileHeader';

export { ProfileHeader };
